import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import { describePages } from '../../utils/lineItemFlags';
import { splitQuestionOpen } from '../../utils/splitQuestion';
import { declineVendorInvoiceSplit, splitVendorInvoice, type VendorInvoice } from '../../lib/vendorInvoiceApi';

// D94: a file that holds 2+ supplier invoices asks whether to split it. YES makes
// each invoice its own record; NO deletes this record and the person uploads each
// invoice separately. Anyone who can edit vendor invoices may answer; the rest see
// the question without the buttons. A record left "declined" (the person said NO but the
// delete failed) shows a notice with the delete action only: the answer is carried out on retry.

/** What the person is told for each refusal the server can give, by `code`, never by its prose. */
const REFUSALS: Record<string, string> = {
  PERMISSION_DENIED: 'You do not have permission to answer this question.',
  SPLIT_NOT_PENDING: 'This file is no longer waiting for an answer. Reload the page to see where it stands.',
  SPLIT_ALREADY_CONFIRMED: 'This invoice has already been confirmed to Inventory, so it can no longer be split.',
  VENDOR_INVOICE_NOT_FOUND: 'This invoice no longer exists. Reload the page.',
};
const GENERIC_REFUSAL = 'Could not complete that. Please try again.';
// The answer WAS taken; only the page's follow-up (reload / leave the screen) failed.
const FOLLOW_UP_FAILED = 'Done. Reload the page to see the result.';

export interface SplitInvoiceQuestionProps {
  invoice: Pick<VendorInvoice, '_id' | 'splitProposal'>;
  canEdit: boolean;
  /** After YES the page re-reads this record and the list (the new records appear in it). */
  onSplit: () => void | Promise<void>;
  /** After NO the record is gone: the page leaves the review screen and shows `message`. */
  onDeleted: (message: string) => void | Promise<void>;
}

const SplitInvoiceQuestion: React.FC<SplitInvoiceQuestionProps> = ({ invoice, canEdit, onSplit, onDeleted }) => {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const proposal = invoice.splitProposal;
  const invoices = proposal?.invoices || [];
  const declined = proposal?.status === 'declined';
  if (!splitQuestionOpen(proposal)) return null;
  const n = invoices.length;

  /** `call` is the API request (its refusal is worded by code); `then` is the page's follow-up. */
  async function run(call: () => Promise<any>, then: (res: any) => void | Promise<void>) {
    setBusy(true);
    setError(null);
    let res: any;
    try {
      res = await call();
    } catch (e: any) {
      setError((e?.code && REFUSALS[e.code]) || GENERIC_REFUSAL);
      setBusy(false);
      return;
    }
    try {
      await then(res);
    } catch {
      setError(FOLLOW_UP_FAILED);
    } finally {
      setBusy(false);
    }
  }

  const yes = () => run(() => splitVendorInvoice(invoice._id), () => onSplit());

  const no = async () => {
    const ok = await confirm({
      title: 'Delete this record?',
      summary: 'It will be deleted, and you will need to upload each invoice in this file separately.',
      tone: 'error',
      confirmLabel: 'Delete this record',
      cancelLabel: 'Keep it',
    });
    if (!ok) return;
    await run(() => declineVendorInvoiceSplit(invoice._id), (res) => onDeleted(res.message));
  };

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-3 space-y-2" data-testid="split-invoice-question">
      {declined ? (
        <div role="alert" className="text-xs text-amber-900">
          This file was answered No, but the record could not be deleted.
        </div>
      ) : (
        <div role="alert" className="text-xs text-amber-900">
          <span className="font-semibold">This file holds {n} supplier invoices: </span>
          {invoices.map((inv, i) => (
            <span key={i}>
              {i > 0 ? ', ' : ''}
              {inv.invoiceNumber || 'no number'}
              {inv.pages?.length ? ` (${describePages(inv.pages)})` : ''}
            </span>
          ))}
          . Split it into {n} records?
        </div>
      )}

      {error ? (
        <div role="alert" className="text-xs text-rose-700">
          {error}
        </div>
      ) : null}

      {canEdit ? (
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={no}
            disabled={busy}
            className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {declined ? 'Delete this record' : 'No — delete this record'}
          </button>
          {declined ? null : (
          <button
            type="button"
            onClick={yes}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary-600 bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy && <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />}
            Split into {n} records
          </button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default SplitInvoiceQuestion;
